'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Permit, PermitStatus } from './types';
import { PIPELINE_STAGES } from './types';
import IdentifyJurisdictionButton, { type JurisdictionResult } from './IdentifyJurisdictionButton';
import InspectionScheduleModal from './InspectionScheduleModal';
import InspectionResultModal from './InspectionResultModal';

interface PermitDetailModalProps {
  permit: Permit | null;
  onClose: () => void;
  jobAddress?: string;
}

type TabKey = 'details' | 'documents' | 'inspections' | 'timeline';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatTimelineDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const formatAction = (action: string): string => {
  const map: Record<string, string> = {
    filed: 'Filed',
    approved: 'Approved',
    passed: 'Passed',
    failed: 'Failed',
    corrections_required: 'Corrections Required',
    closed: 'Closed',
    scheduled_inspection: 'Inspection Scheduled',
    pending_review: 'Submitted for Review',
    jurisdiction_identified: 'Jurisdiction Identified',
    permit_created_via_ai: 'Permit Created via AI',
  };
  if (map[action]) return map[action];
  // Handle dynamic status_changed_to_X entries
  if (action.startsWith('status_changed_to_')) {
    const status = action.replace('status_changed_to_', '').replace(/_/g, ' ');
    return `Status → ${status.charAt(0).toUpperCase() + status.slice(1)}`;
  }
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const timelineDotColor = (action: string): string => {
  if (['filed', 'pending_review', 'scheduled_inspection'].includes(action)) return '#2563eb';
  if (['approved', 'passed', 'jurisdiction_identified', 'permit_created_via_ai'].includes(action)) return '#16a34a';
  if (['corrections_required', 'failed'].includes(action)) return '#dc2626';
  if (['closed'].includes(action)) return '#6b7280';
  if (action.startsWith('status_changed_to_approved') || action.startsWith('status_changed_to_passed')) return '#16a34a';
  if (action.startsWith('status_changed_to_corrections') || action.startsWith('status_changed_to_failed')) return '#dc2626';
  if (action.startsWith('status_changed_to_closed')) return '#6b7280';
  return '#6b7280';
};

const resultChipClass = (result?: string): string => {
  if (!result) return 'bg-gray-100 text-gray-600';
  if (result === 'pass') return 'bg-green-100 text-green-700';
  if (result === 'fail') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

const PermitDetailModal = ({ permit, onClose, jobAddress }: PermitDetailModalProps) => {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [editedStatus, setEditedStatus] = useState<PermitStatus>('needs_filing');
  const [editedAssignee, setEditedAssignee] = useState('');
  const [editedPermitNumber, setEditedPermitNumber] = useState('');
  const [editedParcelNumber, setEditedParcelNumber] = useState('');
  const [editedFeeAmount, setEditedFeeAmount] = useState('');
  const [editedFeePaid, setEditedFeePaid] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Nested modal state
  const [scheduleInspectionOpen, setScheduleInspectionOpen] = useState(false);
  const [recordResultInspectionId, setRecordResultInspectionId] = useState<string | null>(null);

  // Playbook state
  const [playbook, setPlaybook] = useState<Record<string, unknown> | null>(null);
  const [playbookLoading, setPlaybookLoading] = useState(false);

  // Sync state when permit changes
  useEffect(() => {
    if (!permit) return;
    setActiveTab('details');
    setEditedStatus(permit.status);
    setEditedAssignee(permit.assigned_to_name ?? '');
    setEditedPermitNumber(permit.permit_number ?? '');
    setEditedParcelNumber(permit.parcel_number ?? '');
    setEditedFeeAmount(permit.fee_amount?.toString() ?? '');
    setEditedFeePaid(permit.fee_paid);
    setEditedNotes(permit.notes ?? '');
    setSubmitting(false);
    setScheduleInspectionOpen(false);
    setRecordResultInspectionId(null);
  }, [permit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch linked playbook
  useEffect(() => {
    if (!permit?.jurisdiction_playbook_id) {
      setPlaybook(null);
      return;
    }
    let cancelled = false;
    setPlaybookLoading(true);
    supabase
      .from('jurisdiction_playbooks')
      .select('*')
      .eq('id', permit.jurisdiction_playbook_id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setPlaybook(data as Record<string, unknown> | null);
        setPlaybookLoading(false);
      });
    return () => { cancelled = true; };
  }, [permit?.jurisdiction_playbook_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    if (!permit) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [permit, submitting, onClose]);

  if (!permit) return null;

  const handleJurisdictionResult = async (result: JurisdictionResult) => {
    const pb = result.playbook;
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const now = new Date().toISOString();
    const existingTimeline: Array<{ action: string; actor: string; actor_id?: string; timestamp: string; note?: string }> =
      Array.isArray(permit.timeline_log) ? permit.timeline_log : [];

    const parseFee = (formula: string | null): number | null => {
      if (!formula) return null;
      const match = formula.match(/\$([0-9,]+(?:\.[0-9]{1,2})?)/);
      if (!match) return null;
      const n = Number(match[1].replace(/,/g, ''));
      return isNaN(n) ? null : n;
    };

    const updatePayload = {
      jurisdiction_playbook_id: pb.id,
      jurisdiction_name: pb.jurisdiction_name,
      jurisdiction_type: pb.jurisdiction_type,
      permit_portal_url: pb.permit_portal_url ?? null,
      required_documents: pb.required_documents ?? [],
      fee_amount: permit.fee_amount ?? parseFee(pb.fee_formula_text),
      timeline_log: [...existingTimeline, {
        action: 'jurisdiction_identified',
        actor: actorName,
        actor_id: currentUser?.id ?? null,
        timestamp: now,
        note: `${result.source === 'cache' ? 'Loaded from cache' : 'AI-identified'}: ${pb.jurisdiction_name} (confidence ${Math.round((pb.confidence_score ?? 0) * 100)}%)`,
      }],
      updated_at: now,
    };

    try {
      const { error } = await supabase
        .from('permits')
        .update(updatePayload)
        .eq('id', permit.id);
      if (error) throw error;
      toast.success(`Permit updated with ${pb.jurisdiction_name}`, { duration: 3000 });
    } catch (err) {
      console.error('Failed to save jurisdiction result:', err);
      toast.error(`Failed to save jurisdiction: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  };

  const handleSave = async () => {
    if (!permit) return;
    if (submitting) return; // double-click guard
    setSubmitting(true);

    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const now = new Date().toISOString();

    const statusChanged = editedStatus !== permit.status;
    const existingTimeline: Array<{ action: string; actor: string; actor_id?: string; timestamp: string; note?: string }> =
      Array.isArray(permit.timeline_log) ? permit.timeline_log : [];
    const newTimelineEntry = statusChanged
      ? [{
          action: `status_changed_to_${editedStatus}`,
          actor: actorName,
          actor_id: currentUser?.id ?? undefined,
          timestamp: now,
          note: `Changed from ${permit.status} to ${editedStatus}`,
        }]
      : [];

    const updatePayload: Record<string, unknown> = {
      status: editedStatus,
      assigned_to_name: editedAssignee.trim() || null,
      permit_number: editedPermitNumber.trim() || null,
      parcel_number: editedParcelNumber.trim() || null,
      fee_amount: editedFeeAmount.trim() ? Number(editedFeeAmount) : null,
      fee_paid: editedFeePaid,
      notes: editedNotes.trim() || null,
      timeline_log: [...existingTimeline, ...newTimelineEntry],
      updated_at: now,
    };

    if (editedFeePaid && !permit.fee_paid) {
      updatePayload.fee_paid_at = now;
    }

    if (statusChanged) {
      if (editedStatus === 'filed' && !permit.filed_at) updatePayload.filed_at = now;
      if (editedStatus === 'approved' && !permit.approved_at) updatePayload.approved_at = now;
      if (editedStatus === 'closed' && !permit.closed_at) updatePayload.closed_at = now;
    }

    // Use AbortController for a hard timeout — avoids hanging on stuck singleton client
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

      const response = await fetch(
        `${supabaseUrl}/rest/v1/permits?id=eq.${encodeURIComponent(permit.id)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Prefer': 'return=minimal',
          },
          body: JSON.stringify(updatePayload),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => `HTTP ${response.status}`);
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      toast.success(`${permit.job_id} permit updated`, { duration: 3000 });
      setSubmitting(false);
      onClose();
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Failed to update permit:', err);
      const message = err instanceof Error
        ? (err.name === 'AbortError' ? 'Save timed out. Check your network and try again.' : err.message)
        : 'Unknown error';
      toast.error(`Update failed: ${message}`, { duration: 6000 });
      setSubmitting(false);
      // DO NOT close the modal on error — let the user retry or cancel
    }
  };

  const stage = PIPELINE_STAGES.find(s => s.key === permit.status);
  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'details', label: 'Details' },
    { key: 'documents', label: 'Documents' },
    { key: 'inspections', label: 'Inspections' },
    { key: 'timeline', label: 'Timeline' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono font-bold text-xl text-[#1C2E5E]">{permit.job_id}</span>
              {stage && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${stage.bg} ${stage.text}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.dotColor }} />
                  {stage.label}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {permit.jurisdiction_name ?? 'Jurisdiction not identified'}
              {permit.jurisdiction_type && (
                <span className="ml-2 text-[11px] text-gray-400 capitalize">({permit.jurisdiction_type})</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* TABS */}
        <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[13px] font-semibold border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[#E89A3C] text-[#E89A3C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── TAB 1: DETAILS ── */}
          {activeTab === 'details' && (
            <div className="space-y-5">

              {/* Jurisdiction Info section */}
              <div className="mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Jurisdiction</span>
                  {jobAddress && (
                    <IdentifyJurisdictionButton
                      address={jobAddress}
                      variant="compact"
                      label="Re-identify with AI"
                      onSuccess={handleJurisdictionResult}
                      disabled={submitting}
                      onError={(err) => toast.error(`Jurisdiction error: ${err instanceof Error ? err.message : 'Unknown'}`)}
                    />
                  )}
                </div>
                <p className="text-[13px] font-semibold text-[#1C2E5E]">
                  {permit.jurisdiction_name ?? 'Not identified'}
                  {permit.jurisdiction_type && (
                    <span className="ml-2 text-[11px] font-normal text-gray-500">({permit.jurisdiction_type})</span>
                  )}
                </p>
                {jobAddress && (
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate" title={jobAddress}>{jobAddress}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Left column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Status</label>
                    <select
                      value={editedStatus}
                      onChange={(e) => setEditedStatus(e.target.value as PermitStatus)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20"
                    >
                      {(['not_started','needs_filing','filed','pending_review','approved','scheduled_inspection','passed','failed','corrections_required','closed','not_required'] as PermitStatus[]).map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Assigned To</label>
                    <input
                      type="text"
                      value={editedAssignee}
                      onChange={(e) => setEditedAssignee(e.target.value)}
                      placeholder="Assignee name"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Permit Number</label>
                    <input
                      type="text"
                      value={editedPermitNumber}
                      onChange={(e) => setEditedPermitNumber(e.target.value)}
                      placeholder="e.g. BLD-2026-00123"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Parcel Number</label>
                    <input
                      type="text"
                      value={editedParcelNumber}
                      onChange={(e) => setEditedParcelNumber(e.target.value)}
                      placeholder="e.g. 123-456-789"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20"
                    />
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Jurisdiction Type</label>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold bg-gray-100 text-gray-700 capitalize">
                      {permit.jurisdiction_type ?? '—'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Permit Portal</label>
                    {permit.permit_portal_url ? (
                      <a
                        href={permit.permit_portal_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#E89A3C] hover:underline break-all"
                      >
                        {permit.permit_portal_url}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Fee Amount</label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          value={editedFeeAmount}
                          onChange={(e) => setEditedFeeAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <div
                          onClick={() => setEditedFeePaid(p => !p)}
                          className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${editedFeePaid ? 'bg-green-500' : 'bg-gray-300'}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${editedFeePaid ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </div>
                        <span className="text-[12px] font-semibold text-gray-600">Paid</span>
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Filed Date</label>
                      <span className="text-sm text-gray-700">{formatDateTime(permit.filed_at)}</span>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Approved Date</label>
                      <span className="text-sm text-gray-700">{formatDateTime(permit.approved_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes — full width */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                  Notes <span className="text-gray-400 font-normal normal-case">({editedNotes.length}/1000)</span>
                </label>
                <textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value.slice(0, 1000))}
                  rows={4}
                  placeholder="Add notes about this permit…"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 resize-none"
                />
              </div>

              {/* AI Playbook Details — full width */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                {playbook ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-purple-700">AI Playbook</span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-purple-100 text-purple-700">
                        🤖 Confidence {Math.round(((playbook.confidence_score as number) ?? 0) * 100)}%
                      </span>
                      {(playbook.verified_at as string | null) && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700">
                          ✓ Human-verified
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      {(playbook.permit_portal_phone as string | null) && (
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Phone</span>
                          <p className="text-gray-800 mt-0.5">{playbook.permit_portal_phone as string}</p>
                        </div>
                      )}
                      {(playbook.fee_formula_text as string | null) && (
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Fee Formula</span>
                          <p className="text-gray-800 mt-0.5">{playbook.fee_formula_text as string}</p>
                        </div>
                      )}
                      {(playbook.turnaround_days_typical as number | null) && (
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Typical Turnaround</span>
                          <p className="text-gray-800 mt-0.5">{playbook.turnaround_days_typical as number} days</p>
                        </div>
                      )}
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Contractor Reg</span>
                        <p className="text-gray-800 mt-0.5">{(playbook.contractor_registration_required as boolean) ? 'Required' : 'Not required'}</p>
                      </div>
                    </div>
                    {(playbook.contractor_registration_notes as string | null) && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Contractor Reg Notes</span>
                        <p className="text-[12px] text-gray-700 mt-0.5 leading-relaxed">{playbook.contractor_registration_notes as string}</p>
                      </div>
                    )}
                    {(playbook.special_notes as string | null) && (
                      <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">⚠ Special Notes</span>
                        <p className="text-[12px] text-amber-800 mt-0.5 leading-relaxed">{playbook.special_notes as string}</p>
                      </div>
                    )}
                    {Array.isArray(playbook.inspections_required) && (playbook.inspections_required as Array<{ type: string; required: boolean; notes?: string }>).length > 0 && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Inspections Required</span>
                        <ul className="mt-1 space-y-1">
                          {(playbook.inspections_required as Array<{ type: string; required: boolean; notes?: string }>).map((ins, idx) => (
                            <li key={idx} className="text-[12px] text-gray-700 flex items-start gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${ins.required ? 'bg-red-500' : 'bg-gray-300'}`} />
                              <div>
                                <span className="font-semibold">{ins.type}</span>
                                {ins.required ? <span className="text-red-700 ml-1">(required)</span> : <span className="text-gray-500 ml-1">(not required)</span>}
                                {ins.notes && <span className="block text-gray-500 text-[11px] mt-0.5">{ins.notes}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(playbook.source_urls) && (playbook.source_urls as string[]).length > 0 && (
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Sources</span>
                        <ul className="mt-1 space-y-0.5">
                          {(playbook.source_urls as string[]).slice(0, 4).map((url, idx) => (
                            <li key={idx}>
                              <a href={url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#E89A3C] hover:underline truncate block" title={url}>
                                {url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : playbookLoading ? (
                  <p className="text-[12px] text-gray-500 italic">Loading AI playbook…</p>
                ) : (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 border border-purple-100">
                    <svg className="w-4 h-4 text-purple-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" strokeLinecap="round" />
                      <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                    </svg>
                    <p className="text-[12px] text-purple-800">
                      No AI playbook linked. Click <strong>Re-identify with AI</strong> above to generate one.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 2: DOCUMENTS ── */}
          {activeTab === 'documents' && (
            <div className="space-y-3">
              <p className="text-[12px] text-gray-500 mb-3">Required documents for this permit</p>
              {(permit.required_documents ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No required documents listed.</p>
              ) : (
                (permit.required_documents ?? []).map((docName, idx) => {
                  const uploaded = (permit.uploaded_documents ?? []).find(
                    u => u.name.toLowerCase() === docName.toLowerCase()
                  );
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-3">
                        {uploaded ? (
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" className="w-3.5 h-3.5">
                              <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0" />
                        )}
                        <div>
                          <p className="text-[13px] font-semibold text-gray-700">{docName}</p>
                          {uploaded && <p className="text-[11px] text-gray-400">{uploaded.name}</p>}
                        </div>
                      </div>
                      {uploaded ? (
                        <a href={uploaded.url} target="_blank" rel="noopener noreferrer" className="text-[12px] font-semibold text-[#E89A3C] hover:underline">View</a>
                      ) : (
                        <button disabled className="text-[12px] font-semibold text-gray-400 px-3 py-1 rounded-lg border border-gray-200 cursor-not-allowed" title="Upload available in P4">Upload</button>
                      )}
                    </div>
                  );
                })
              )}
              <div className="pt-2">
                <button disabled className="text-[12px] font-semibold text-gray-400 px-4 py-2 rounded-lg border border-dashed border-gray-300 w-full cursor-not-allowed" title="Upload available in P4">
                  + Upload new document
                </button>
              </div>
            </div>
          )}

          {/* ── TAB 3: INSPECTIONS ── */}
          {activeTab === 'inspections' && (
            <div className="space-y-4">
              {/* Header with Schedule button */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-[13px] font-bold text-[#1C2E5E]">Inspections</h4>
                  <p className="text-[11px] text-gray-500">Schedule and record mid-roof, final, and re-inspections for this permit.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setScheduleInspectionOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" strokeLinecap="round"/>
                  </svg>
                  Schedule Inspection
                </button>
              </div>

              {/* Inspection list */}
              {(!permit.inspections || permit.inspections.length === 0) ? (
                <div className="p-6 text-center rounded-lg border border-dashed border-gray-200">
                  <svg className="w-8 h-8 mx-auto text-gray-300 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <path d="M16 2v4M8 2v4M3 10h18"/>
                  </svg>
                  <p className="text-[13px] font-semibold text-gray-600">No inspections scheduled yet</p>
                  <p className="text-[11px] text-gray-400 mt-1">Click &quot;Schedule Inspection&quot; to book the first visit.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...permit.inspections]
                    .sort((a, b) => {
                      const aDate = new Date(a.scheduled_at).getTime();
                      const bDate = new Date(b.scheduled_at).getTime();
                      return bDate - aDate;
                    })
                    .map(ins => {
                      const resultConfig: Record<string, { label: string; bg: string; text: string; icon: string }> = {
                        pending:          { label: 'Pending',          bg: 'bg-gray-100',  text: 'text-gray-700',  icon: '⏳' },
                        pass:             { label: 'Passed',           bg: 'bg-green-100', text: 'text-green-700', icon: '✅' },
                        conditional_pass: { label: 'Conditional Pass', bg: 'bg-amber-100', text: 'text-amber-700', icon: '⚠️' },
                        fail:             { label: 'Failed',           bg: 'bg-red-100',   text: 'text-red-700',   icon: '❌' },
                      };
                      const cfg = resultConfig[ins.result] ?? resultConfig.pending;
                      const typeConfig: Record<string, { bg: string; text: string; label: string }> = {
                        'mid-roof':     { bg: 'bg-blue-50',   text: 'text-blue-700',  label: 'Mid-Roof' },
                        'final':        { bg: 'bg-green-50',  text: 'text-green-700', label: 'Final' },
                        'reinspection': { bg: 'bg-amber-50',  text: 'text-amber-700', label: 'Re-inspection' },
                        'other':        { bg: 'bg-gray-50',   text: 'text-gray-700',  label: 'Other' },
                      };
                      const tcfg = typeConfig[ins.type] ?? typeConfig.other;
                      const scheduledDate = new Date(ins.scheduled_at);
                      const isPending = ins.result === 'pending';

                      return (
                        <div key={ins.id} className="rounded-lg border border-gray-200 bg-white p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${tcfg.bg} ${tcfg.text}`}>
                                {tcfg.label}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.bg} ${cfg.text}`}>
                                <span>{cfg.icon}</span>
                                {cfg.label}
                              </span>
                            </div>
                            {isPending && (
                              <button
                                type="button"
                                onClick={() => setRecordResultInspectionId(ins.id)}
                                className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#E89A3C] text-white hover:bg-[#0891c9] transition-colors flex-shrink-0"
                              >
                                Record Result
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-[11px]">
                            <div>
                              <span className="text-gray-500 uppercase tracking-wide font-semibold text-[9px]">Scheduled</span>
                              <p className="text-gray-800 font-medium mt-0.5">
                                {scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                {' · '}
                                {scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              </p>
                            </div>
                            {ins.inspector && (
                              <div>
                                <span className="text-gray-500 uppercase tracking-wide font-semibold text-[9px]">Inspector</span>
                                <p className="text-gray-800 font-medium mt-0.5 truncate">{ins.inspector}</p>
                              </div>
                            )}
                            {ins.completed_at && (
                              <div className="col-span-2">
                                <span className="text-gray-500 uppercase tracking-wide font-semibold text-[9px]">Completed</span>
                                <p className="text-gray-800 font-medium mt-0.5">
                                  {new Date(ins.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </p>
                              </div>
                            )}
                          </div>

                          {ins.corrections && (
                            <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-100">
                              <p className="text-[10px] font-bold uppercase tracking-wide text-red-700 mb-0.5">⚠ Corrections Required</p>
                              <p className="text-[11px] text-red-800 leading-relaxed">{ins.corrections}</p>
                            </div>
                          )}

                          {ins.notes && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-0.5">Notes</p>
                              <p className="text-[11px] text-gray-700 leading-relaxed">{ins.notes}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* ── TAB 4: TIMELINE ── */}
          {activeTab === 'timeline' && (
            <div>
              {(permit.timeline_log ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No timeline events yet.</p>
              ) : (
                <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5">
                  {[...(permit.timeline_log ?? [])].reverse().map((entry, idx) => (
                    <li key={idx} className="ml-5">
                      <span className="absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white" style={{ background: timelineDotColor(entry.action) }} />
                      <p className="text-[11px] text-gray-400">{formatTimelineDate(entry.timestamp)}</p>
                      <p className="text-[13px] font-semibold text-gray-800">{formatAction(entry.action)}</p>
                      <p className="text-[12px] text-gray-500">{entry.actor}</p>
                      {entry.note && <p className="text-[12px] text-gray-500 italic mt-0.5">{entry.note}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2"
            style={{ background: submitting ? '#6b7280' : '#E89A3C' }}
          >
            {submitting && (
              <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
            )}
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Nested modals */}
      <InspectionScheduleModal
        permit={scheduleInspectionOpen ? permit : null}
        onClose={() => setScheduleInspectionOpen(false)}
        onScheduled={() => { /* realtime subscription in parent auto-refreshes */ }}
      />

      <InspectionResultModal
        permit={recordResultInspectionId ? permit : null}
        inspectionId={recordResultInspectionId}
        onClose={() => setRecordResultInspectionId(null)}
        onRecorded={() => { /* realtime subscription auto-refreshes */ }}
      />
    </div>
  );
};

export default PermitDetailModal;
